import { API } from "@apis/index";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import useEscapeKey from "@hooks/useEscapeKey";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnMatchIcon, MatchIcon } from "@components/Icons";
import { createPortal } from "react-dom";
import { OutlineButton } from "@components/Buttons";

const LicenseInformationModal = ({ show, onClose, componentLicense, "data-testid": dataTestId = "license-information-modal" }) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [licenseData, setLicenseData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEscapeKey(onClose);

  useEffect(() => {
    if (show && componentLicense) {
      fetchLicenseData();
    }
  }, [show, componentLicense]);

  const fetchLicenseData = async () => {
    if (!componentLicense) return;

    setLoading(true);
    try {
      const response = await API.get(`license_detail/${componentLicense}`);
      if (!response.data.license) {
        setLicenseData(null);
        return;
      }
      setLicenseData(response.data);
    } catch (error) {
      console.error("Error fetching license data:", error);
      showErrorToast(t("toast.loading-data-error"));
    } finally {
      setLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  const modalContent = (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} w-[760px] min-w-[760px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <h2 className="text-xl font-bold text-center text-white uppercase tracking-wide" data-testid={`${dataTestId}-title`}>
            {t("open-source.license-information-title")}
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4 pb-4 flex-1 min-h-0 overflow-y-auto" data-testid={`${dataTestId}-content`}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : licenseData ? (
            <div className="space-y-0">
              {/* License Details Section */}
              <div className={`border ${colors.borderPrimary} rounded-t-lg overflow-hidden`}>
                <h4 className={`px-4 py-3 text-base uppercase font-semibold ${colors.textSecondary}  ${colors.borderPrimary}`}>
                  {t("open-source.license-details")}
                </h4>

                {/* License Identifier */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-identifier")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-identifier")}>
                    {t("open-source.license-identifier")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-identifier")}>
                    {licenseData.license.licenseIdentifier || "---"}
                  </div>
                </div>

                {/* License Name */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-name")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-name")}>
                    {t("open-source.license-name")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-name")}>
                    {licenseData.license.licenseName || "---"}
                  </div>
                </div>

                {/* License Category */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-category")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-category")}>
                    {t("open-source.license-category")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-category")}>
                    {licenseData.licenseAttribute?.licenseTypeCdName || "---"}
                  </div>
                </div>

                {/* Homepage URL */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("homepage-url")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("homepage-url")}>
                    {t("open-source.homepage-url")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("homepage-url")}>
                    {licenseData.license.homepageUrl ? (
                      <a
                        href={licenseData.license.homepageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline`}
                      >
                        {licenseData.license.homepageUrl}
                      </a>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>

                {/* License Header */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-header")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-header")}>
                    {t("open-source.license-header")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-header")}>
                    {licenseData.license.licenseHeader ? (
                      <div className={`overflow-y-auto`} style={{ height: "6rem" }}>
                        {licenseData.license.licenseHeader}
                      </div>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>

                {/* License Subname */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-subname")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-subname")}>
                    {t("open-source.license-subname")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-subname")}>
                    {licenseData.license.licenseSubName || "---"}
                  </div>
                </div>

                {/* License Scope */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-scope")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-scope")}>
                    {t("open-source.license-scope")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-scope")}>
                    {licenseData.license.licenseScope || "---"}
                  </div>
                </div>

                {/* Obligation Type */}
                <div className={`flex ${colors.borderPrimary}`} data-testid={fieldContainerTestId("obligation-type")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("obligation-type")}>
                    {t("component.obligation-type")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("obligation-type")}>
                    {licenseData.license.obligationTypeCd || "---"}
                  </div>
                </div>
              </div>

              {/* Technical Attributes Section */}
              <div className={`border ${colors.borderPrimary} border-t-0 overflow-hidden`}>
                <h4 className={`px-4 py-3 text-base font-semibold uppercase ${colors.textSecondary}`}>{t("open-source.technical-attributes")}</h4>

                <div className="grid grid-cols-2">
                  {/* Left Column */}
                  <div className="">

                    <div className={`flex py-4 px-4`} data-testid={fieldContainerTestId("copyleft")}>
                      <div className="w-4/6 flex-shrink-0">
                        <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldLabelTestId("copyleft")}>
                          {t("open-source.copyleft")}
                        </span>
                      </div>
                      <div className="w-2/6 flex items-center justify-start" data-testid={fieldValueTestId("copyleft")}>
                        {licenseData.license.copyleftFlag === "Y" ? (
                          <MatchIcon />
                        ) : (
                          <UnMatchIcon />
                        )}
                      </div>
                    </div>

                    <div className={`flex py-4 px-4`} data-testid={fieldContainerTestId("deprecation")}>
                      <div className="w-4/6 flex-shrink-0">
                        <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldLabelTestId("deprecation")}>
                          {t("open-source.deprecation")}
                        </span>
                      </div>
                      <div className="w-2/6 flex items-center justify-start" data-testid={fieldValueTestId("deprecation")}>
                        {licenseData.license.deprecationFlag === "Y" ? (
                          <MatchIcon />
                        ) : (
                          <UnMatchIcon />
                        )}
                    </div>
                  </div>

                    <div className={`flex py-4 px-4`} data-testid={fieldContainerTestId("spdx-standard")}>
                      <div className="w-4/6 flex-shrink-0">
                        <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldLabelTestId("spdx-standard")}>
                          {t("open-source.spdx-standard")}
                        </span>
                      </div>
                      <div className="w-2/6 flex items-center justify-start" data-testid={fieldValueTestId("spdx-standard")}>
                        {licenseData.license.spdxStandardFlag === "Y" ? (
                          <MatchIcon />
                        ) : (
                          <UnMatchIcon />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="">                   
                    <div className={`flex py-4 px-4`} data-testid={fieldContainerTestId("osi-approval")}>
                      <div className="w-4/6 flex-shrink-0">
                        <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldLabelTestId("osi-approval")}>
                          {t("open-source.osi-approval")}
                        </span>
                      </div>
                      <div className="w-2/6 flex items-center justify-start" data-testid={fieldValueTestId("osi-approval")}>
                        {licenseData.license.osiApprovalFlag === "Y" ? (
                          <MatchIcon />
                        ) : (
                          <UnMatchIcon />
                        )}
                      </div>
                    </div>

                    <div className={`flex py-4 px-4`} data-testid={fieldContainerTestId("including-in-report")}>
                      <div className="w-4/6 flex-shrink-0">
                        <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldLabelTestId("including-in-report")}>
                          {t("open-source.including-in-report")}
                        </span>
                      </div>
                      <div className="w-2/6 flex items-center justify-start" data-testid={fieldValueTestId("including-in-report")}>
                        {licenseData.license.includingInReportFlag === "Y" ? (
                          <MatchIcon />
                        ) : (
                          <UnMatchIcon />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descriptive Notes Section */}
              <div className={`border ${colors.borderPrimary} border-t-0 rounded-b-lg overflow-hidden`}>
                <h4 className={`px-4 py-3 text-base uppercase font-semibold ${colors.textSecondary}`}>{t("open-source.descriptive-notes")}</h4>

                {/* License Note */}
                <div className={`flex border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-note")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-note")}>
                    {t("open-source.license-note")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-note")}>
                    {licenseData.license.licenseNote || "---"}
                  </div>
                </div>

                {/* License Comment */}
                <div className={`flex border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-comment")}>
                  <div className={`px-4 py-3 text-sm font-medium ${colors.textSecondary}`} style={{ width: "10rem" }} data-testid={fieldLabelTestId("license-comment")}>
                    {t("open-source.license-comment")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-comment")}>
                    {licenseData.license.licenseComment ? (
                      <div className={`overflow-y-auto`} style={{ height: "6rem" }}>
                        {licenseData.license.licenseComment}
                      </div>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>

                {/* License Text */}
                <div className={`flex`} data-testid={fieldContainerTestId("license-text")}>
                  <div
                    className={`px-4 py-3 text-sm font-medium ${colors.textSecondary} align-top`}
                    style={{ width: "10rem" }}
                    data-testid={fieldLabelTestId("license-text")}
                  >
                    {t("open-source.license-text")}
                  </div>
                  <div className={`flex-1 px-4 py-3 text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-text")}>
                    {licenseData.license.licenseText ? (
                      <div className={`overflow-y-auto`} style={{ height: "6rem" }}>
                        {licenseData.license.licenseText}
                      </div>
                    ) : (
                      "---"
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 text-white/80`}>{t("common.no-data-to-display")}</div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${colors.headerDividerBorder} flex justify-center flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>{t("common.close-button")}</OutlineButton>
        </div>
      </div>
    </div>
  );
  return createPortal(modalContent, document.body);
};

export default LicenseInformationModal;
